import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { ArrowLeft, Loader, CheckCircle, XCircle, RefreshCw, AlertTriangle } from 'lucide-react';

const RouterDiagnosticsPage = () => {
  const navigate = useNavigate();
  const { id: routerId } = useParams();
  const { isDark } = useTheme();
  const { user } = useAuth();
  
  const [router, setRouter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    loadRouterData();
  }, [routerId]);

  const loadRouterData = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/routers/${routerId}`);
      setRouter(response.data);
      
      // Automáticamente hacer test al cargar
      await runDiagnostics(response.data);
    } catch (error) {
      console.error('Error cargando router:', error);
      alert('No se encontró el router');
      navigate('/routers');
    } finally {
      setLoading(false);
    }
  };

  const runDiagnostics = async (routerData = router) => {
    if (!routerData) return;
    
    try {
      setTesting(true);
      const response = await api.post('/api/routers/test-connection', {
        name: routerData.name,
        ip_address: routerData.ip_address,
        username: routerData.username,
        password: routerData.password,
        port: routerData.port || 8728
      });
      
      setTestResult(response.data);
    } catch (error) {
      console.error('Error en diagnóstico:', error);
      setTestResult({
        ip_reachable: false,
        port_open: false,
        api_responds: false,
        details: ['❌ Error al realizar diagnóstico', error.response?.data?.detail || 'Error desconocido']
      });
    } finally {
      setTesting(false);
    }
  };

  const handleReconnect = async () => {
    // Obtener credenciales VPN actuales del router desde la licencia
    try {
      const licenseResponse = await api.get('/api/licenses/my-routers');
      const routerCreds = licenseResponse.data.routers.find(r => 
        r.vpn_internal_ip === router.ip_address
      );
      
      if (!routerCreds) {
        alert('No se encontraron las credenciales VPN de este router');
        return;
      }
      
      // Navegar al paso 1 del script con los datos del router y modo reconexión
      navigate('/router-step1-script', {
        state: {
          router: {
            router_index: routerCreds.router_index,
            vpn_username: routerCreds.vpn_username,
            vpn_password: routerCreds.vpn_password,
            internal_ip: routerCreds.vpn_internal_ip,
            vpn_external_port: routerCreds.vpn_external_port
          },
          reconnectMode: true,
          existingRouter: router  // Router ya guardado con su nombre
        }
      });
    } catch (error) {
      console.error('Error:', error);
      alert('Error al cargar credenciales VPN');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader className="w-12 h-12 animate-spin text-blue-600" />
      </div>
    );
  }

  const isOnline = testResult?.api_responds;

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'} p-6`}>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate('/routers')}
            className={`p-2 rounded-lg ${isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-200'}`}
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div>
            <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              🔍 Diagnóstico de Router
            </h1>
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              {router?.name}
            </p>
          </div>
        </div>

        {/* Estado general */}
        <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-lg p-6 mb-6`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Estado de Conexión
            </h3>
            <button
              onClick={() => runDiagnostics()}
              disabled={testing}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${testing ? 'animate-spin' : ''}`} />
              {testing ? 'Probando...' : 'Probar Ahora'}
            </button>
          </div>

          {testResult && (
            <div className={`p-4 rounded-lg ${
              isOnline 
                ? (isDark ? 'bg-green-900/20 border border-green-500/30' : 'bg-green-50 border border-green-200')
                : (isDark ? 'bg-red-900/20 border border-red-500/30' : 'bg-red-50 border border-red-200')
            }`}>
              <div className="flex items-center gap-3 mb-3">
                {isOnline ? (
                  <>
                    <CheckCircle className="w-6 h-6 text-green-600" />
                    <span className={`font-bold ${isDark ? 'text-green-400' : 'text-green-800'}`}>
                      ✅ Router en línea y funcionando
                    </span>
                  </>
                ) : (
                  <>
                    <XCircle className="w-6 h-6 text-red-600" />
                    <span className={`font-bold ${isDark ? 'text-red-400' : 'text-red-800'}`}>
                      ❌ Router sin conexión
                    </span>
                  </>
                )}
              </div>
              
              <div className={`space-y-1 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                {testResult.details?.map((detail, idx) => (
                  <p key={idx}>{detail}</p>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Datos del router */}
        <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-lg p-6 mb-6`}>
          <h3 className={`text-lg font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            📊 Información del Router
          </h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Nombre:</span>
              <p className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{router?.name}</p>
            </div>
            <div>
              <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>IP:</span>
              <p className={`font-mono font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{router?.ip_address}</p>
            </div>
            <div>
              <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Usuario:</span>
              <p className={`font-mono font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{router?.username}</p>
            </div>
            <div>
              <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Puerto:</span>
              <p className={`font-mono font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{router?.port || 8728}</p>
            </div>
          </div>
        </div>

        {/* Soluciones si está offline */}
        {testResult && !isOnline && (
          <div className={`${isDark ? 'bg-yellow-900/20 border-yellow-500/30' : 'bg-yellow-50 border-yellow-200'} border rounded-xl p-6 mb-6`}>
            <h4 className={`font-semibold mb-3 flex items-center gap-2 ${isDark ? 'text-yellow-400' : 'text-yellow-900'}`}>
              <AlertTriangle className="w-5 h-5" />
              ¿Qué revisar?
            </h4>
            <ul className={`space-y-2 text-sm ${isDark ? 'text-yellow-200' : 'text-yellow-800'}`}>
              <li>• Verifica que el MikroTik tenga internet</li>
              <li>• Ve a Interfaces → L2TP Client → VPN-ETWEB debe estar "connected"</li>
              <li>• Verifica que el servicio API esté habilitado (IP Services)</li>
              <li>• Si la VPN se desconectó, usa el botón "Reconectar" abajo</li>
            </ul>
          </div>
        )}

        {/* Botones de acción */}
        <div className="flex gap-4">
          <button
            onClick={() => navigate('/routers')}
            className={`px-6 py-3 rounded-lg font-semibold ${
              isDark ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
            }`}
          >
            <ArrowLeft className="w-5 h-5 inline mr-2" />
            Volver a Routers
          </button>
          
          {testResult && !isOnline && (
            <button
              onClick={handleReconnect}
              className="flex-1 bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-orange-700 flex items-center justify-center gap-2"
            >
              <RefreshCw className="w-5 h-5" />
              Reconectar Router
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default RouterDiagnosticsPage;
